import { useMutation, UseMutationOptions, useQuery, UseQueryOptions } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { useMemo } from 'react'
import { useHistory } from 'react-router-dom'
import { toast } from 'react-toastify'
import i18n from '../../i18n'
import { mapValuesToSelectOptions } from '../../utils/formUtils/forms'
import { ContactType, DeleteMultipleResponse, PageListParams } from '../globalTypes'
import { invalidateQueries, updateToast } from '../helpers'
import {
    createUpdateContact,
    deleteContactById,
    deleteContacts,
    fetchContactValues,
    getCompanyById,
    getContactById,
    getContactPropertyTransactions,
    getContacts,
} from './functions'
import {
    ContactCriteria,
    ContactData,
    ContactDeleteParams,
    ContactMutationResponse,
    ContactParams,
    ContactPayload,
    ContactPropertyTransactionListResponse,
    ContactsDataList,
    ContactsDeleteParams,
    ContactValuesData,
} from './types'

const contacts = {
    GetContact: {
        useQuery: (id: string, params?: ContactParams, options?: Omit<UseQueryOptions<ContactData, AxiosError>, 'queryKey' | 'queryFn'>) =>
            useQuery({
                queryKey: params ? ['contacts', id, params] : ['contacts', id],
                queryFn: () => getContactById(id, params),
                staleTime: 1 * 60 * 1000,
                ...options,
            }),
    },
    GetCompany: {
        useQuery: (id: string, params?: ContactParams, options?: Omit<UseQueryOptions<ContactData, AxiosError>, 'queryKey' | 'queryFn'>) =>
            useQuery({
                queryKey: params ? ['companies', id, params] : ['companies', id],
                queryFn: () => getCompanyById(id, params),
                staleTime: 1 * 60 * 1000,
                ...options,
            }),
    },
    GetContacts: {
        useQuery: (params: PageListParams<ContactCriteria>, options?: Omit<UseQueryOptions<ContactsDataList, AxiosError>, 'queryKey' | 'queryFn'>) => {
            return useQuery({
                queryKey: ['contacts', 'list', params],
                queryFn: () => getContacts(params),
                staleTime: 1 * 60 * 1000,
                ...options,
                enabled: options?.enabled ?? true,
            })
        },
    },
    GetContactPropertyTransactions: {
        useQuery: (
            contactId: number,
            options?: Omit<UseQueryOptions<ContactPropertyTransactionListResponse, AxiosError>, 'queryKey' | 'queryFn'>
        ) => {
            return useQuery({
                queryKey: ['contacts', 'propertyTransactions', contactId],
                queryFn: () => getContactPropertyTransactions(contactId),
                staleTime: 1 * 60 * 1000,
                ...options,
                enabled: options?.enabled ?? true,
            })
        },
    },
    CreateUpdateContact: {
        useMutation: (options?: UseMutationOptions<ContactMutationResponse, AxiosError, ContactPayload>) => {
            const history = useHistory()

            return useMutation({
                onMutate: () => {
                    const toastId = toast.loading(i18n.t('toast.loading') as string)
                    return toastId
                },
                mutationFn: (payload: ContactPayload) => createUpdateContact(payload),
                onSuccess: (data, variables, context) => {
                    const { isEdit, contactType } = variables
                    const toastId = context as number
                    toastId && updateToast(toastId, i18n.t('toast.update.success') as string, 'success')

                    if (isEdit) {
                        invalidateQueries(['contacts', data.id.toString()])
                        invalidateQueries(['companies', data.id.toString()])
                    } else {
                        const redirectPath = contactType === 'company' ? `/companies/${data.id}/edit` : `/contacts/${data.id}/edit`
                        history.push(redirectPath)
                    }

                    invalidateQueries(['contacts', 'list'])
                    invalidateQueries(['companyAssociate'])
                },
                onError: (error, variables, context) => {
                    const toastId = context as number
                    toastId && updateToast(toastId, i18n.t('toast.update.error') as string, 'error')
                },
                ...options,
            })
        },
    },
    DeleteContact: {
        useMutation: (options?: UseMutationOptions<ContactMutationResponse, AxiosError, ContactDeleteParams>) => {
            const history = useHistory()

            return useMutation({
                onMutate: (params: ContactDeleteParams) => {
                    toast.loading(i18n.t('toast.loading.delete') as string, { toastId: params.id })
                },
                mutationFn: (params: ContactDeleteParams) => deleteContactById(params.id),
                onSuccess: (data, params) => {
                    updateToast(params.id, i18n.t('toast.update.delete.success') as string, 'success')

                    invalidateQueries(['contacts', 'list'])
                    invalidateQueries(['companyAssociate'])
                    invalidateQueries(['notifications', 'deliveries', 'unread-count'])

                    history.push(params.contactType === ContactType.Company ? '/companies' : '/contacts')
                },
                onError: (error, params) => {
                    updateToast(params.id, i18n.t('toast.update.delete.error') as string, 'error')
                },
                ...options,
            })
        },
    },
    DeleteContacts: {
        useMutation: (options?: UseMutationOptions<DeleteMultipleResponse, AxiosError, ContactsDeleteParams>) => {
            return useMutation({
                onMutate: (params: ContactsDeleteParams) => {
                    const { ids } = params
                    toast.loading(i18n.t('toast.loading.delete.contact.number', { requestsToBeDeletedCount: ids.length }) as string, {
                        toastId: ids[0],
                    })
                },
                mutationFn: (params: ContactsDeleteParams) => deleteContacts(params.ids),
                onSuccess: (data, params) => {
                    const { ids } = params ?? {}

                    const failedCount = data?.notFoundIds?.length
                    const successCount = data?.successfulDelete?.length
                    const requestsToBeDeletedCount = ids?.length

                    const successMessage = i18n.t('toast.loading.delete.contact.number.success', {
                        successCount,
                        requestsToBeDeletedCount,
                    }) as string
                    const partialErrorMessage = i18n.t('toast.loading.delete.contact.number.error', {
                        failedCount,
                        requestsToBeDeletedCount,
                    }) as string

                    if (failedCount > 0) {
                        updateToast(ids?.[0], partialErrorMessage, 'error')
                    }

                    if (successCount > 0) {
                        updateToast(ids?.[0], successMessage, 'success')

                        invalidateQueries(['contacts', 'list'])
                        invalidateQueries(['companyAssociate'])
                        invalidateQueries(['notifications', 'deliveries', 'unread-count'])
                    }
                },
                onError: (error, params) => {
                    updateToast(params.ids[0], i18n.t('toast.update.delete.error') as string, 'error')
                },
                ...options,
            })
        },
    },
    FormValues: {
        useQuery: (
            fields?: string[],
            formFilters?: Record<string, string | string[]>,
            options?: Omit<UseQueryOptions<ContactValuesData, AxiosError>, 'queryKey' | 'queryFn'>
        ) => {
            const query = useQuery({
                queryKey: ['contactValues', fields, formFilters],
                queryFn: () => fetchContactValues(fields, formFilters),
                staleTime: Infinity,
                gcTime: Infinity,
                ...options,
            })

            const formattedData = useMemo(() => {
                return mapValuesToSelectOptions(query.data)
            }, [query.data])

            return {
                ...query,
                data: formattedData,
            }
        },
    },
}

export default contacts
